import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import { ToastContainer, toast, Zoom } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import baseURL from "../../Services/Url";
import FirstNavbar from "../dashboard/FirstNavbar";
import SideBar from "../dashboard/SideBar";

const emptyForm = {
  city: "",
  requirement_type: "",
  property_type: "",
  name: "",
  contact_no: "",
  email: "",
  description: "",
  status: "new",
  notes: "",
};

const requirementTypes = ["Buy", "Sell", "Lease", "Interior"];
const propertyTypes = ["Office", "Retail", "Warehouse", "Residential", "Land"];
const statuses = ["new", "contacted", "qualified", "closed", "archived"];

const authConfig = () => {
  try {
    const admin = JSON.parse(localStorage.getItem("admin") || "{}");
    return admin.token
      ? { headers: { Authorization: admin.token } }
      : {};
  } catch {
    return {};
  }
};

function ListRequirement() {
  const [rowData, setRowData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const loadRequirements = useCallback(() => {
    setLoader(true);
    Promise.allSettled([
      axios.get(`${baseURL}/requirements`, authConfig()),
      axios.get(`${baseURL}/category/contactus`, authConfig()),
    ])
      .then(([requirementsResult, contactResult]) => {
        if (requirementsResult.status === "rejected" && contactResult.status === "rejected") {
          throw requirementsResult.reason;
        }

        const requirementData = requirementsResult.status === "fulfilled"
          ? requirementsResult.value.data
          : [];
        const contactData = contactResult.status === "fulfilled"
          ? contactResult.value.data
          : [];
        const requirements = Array.isArray(requirementData)
          ? requirementData.map((item) => ({
              ...item,
              source: "Requirement",
              sourceType: "requirement",
            }))
          : [];
        const contacts = Array.isArray(contactData)
          ? contactData.map((item) => ({
              ...item,
              contact_no: item.contact_no || item.mobile_no || "",
              requirement_type: "Contact Us",
              status: item.status || "new",
              source: "Contact Us",
              sourceType: "contactus",
            }))
          : [];

        setRowData([...requirements, ...contacts].sort((a, b) =>
          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        ));
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Unable to load requirements");
      })
      .finally(() => setLoader(false));
  }, []);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  const filteredData = useMemo(() => {
    if (!startDate && !endDate) return rowData;

    const from = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const to = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;

    return rowData.filter((item) => {
      const createdAt = new Date(item.createdAt).getTime();
      if (Number.isNaN(createdAt)) return false;
      if (from !== null && createdAt < from) return false;
      if (to !== null && createdAt > to) return false;
      return true;
    });
  }, [endDate, rowData, startDate]);

  const openEdit = (item) => {
    setLoader(true);
    axios
      .get(`${baseURL}/requirements/${item.id}`, authConfig())
      .then((response) => {
        const requirement = response.data || item;
        setEditingId(requirement.id);
        setForm({
          ...emptyForm,
          ...requirement,
          requirement_type:
            requirement.requirement_type || requirement.requirment_type || "",
        });
        setShowForm(true);
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Unable to load requirement");
      })
      .finally(() => setLoader(false));
  };

  const updateField = (event) => {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const saveRequirement = () => {
    const requiredFields = [
      "city",
      "requirement_type",
      "property_type",
      "name",
      "contact_no",
      "email",
    ];
    if (requiredFields.some((field) => !String(form[field] || "").trim())) {
      toast.error("Please complete all required fields");
      return;
    }

    setLoader(true);
    axios({
      method: "PUT",
      url: `${baseURL}/requirements/${editingId}`,
      data: form,
      ...authConfig(),
    })
      .then(() => {
        toast.success("Requirement updated");
        setShowForm(false);
        setForm(emptyForm);
        setEditingId("");
        loadRequirements();
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Unable to save requirement");
      })
      .finally(() => setLoader(false));
  };

  const confirmDelete = () => {
    if (!deleteItem?.id) return;
    setLoader(true);
    const deleteUrl = deleteItem.sourceType === "contactus"
      ? `${baseURL}/${deleteItem.id}`
      : `${baseURL}/requirements/${deleteItem.id}`;
    axios
      .delete(deleteUrl, authConfig())
      .then(() => {
        toast.success("Requirement deleted");
        setDeleteItem(null);
        loadRequirements();
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Unable to delete requirement");
      })
      .finally(() => setLoader(false));
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "#",
        valueGetter: "node.rowIndex + 1",
        maxWidth: 80,
        filter: false,
      },
      { headerName: "Name", field: "name" },
      { headerName: "Source", field: "source" },
      { headerName: "City", field: "city" },
      { headerName: "Requirement", field: "requirement_type" },
      { headerName: "Property Type", field: "property_type" },
      { headerName: "Contact No", field: "contact_no" },
      { headerName: "Email", field: "email", minWidth: 210 },
      { headerName: "Description", field: "description", minWidth: 240 },
      { headerName: "Status", field: "status" },
      {
        headerName: "Date",
        field: "createdAt",
        minWidth: 150,
        valueFormatter: ({ value }) =>
          value
            ? new Date(value).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "",
      },
      {
        headerName: "Actions",
        minWidth: 170,
        filter: false,
        sortable: false,
        cellRendererFramework: ({ data }) => (
          <div className="d-flex gap-2 py-1">
            {data.sourceType === "requirement" && (
              <Button size="sm" variant="outline-primary" onClick={() => openEdit(data)}>
                Edit
              </Button>
            )}
            <Button size="sm" variant="outline-danger" onClick={() => setDeleteItem(data)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const defaultColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    flex: 1,
    resizable: true,
    minWidth: 130,
  };

  return (
    <div>
      <ToastContainer position="top-center" autoClose={1800} hideProgressBar transition={Zoom} />
      <div className="sidebar">
        <SideBar />
      </div>
      <div className="content">
        <div className="container">
          <FirstNavbar />
          <div className="d-flex justify-content-between align-items-center my-3">
            <h3><b>List Requirements</b></h3>
            <div className="d-flex gap-2">
              {filteredData.length > 0 && (
                <Button variant="outline-primary">
                  <CSVLink data={filteredData} filename="list-requirements.csv" className="text-decoration-none">
                    Export CSV
                  </CSVLink>
                </Button>
              )}
            </div>
          </div>

          <div className="d-flex gap-2 align-items-end mb-3">
            <div>
              <Form.Label>From</Form.Label>
              <DatePicker selected={startDate} onChange={setStartDate} selectsStart startDate={startDate} endDate={endDate} dateFormat="yyyy-MM-dd" className="form-control" placeholderText="Start date" />
            </div>
            <div>
              <Form.Label>To</Form.Label>
              <DatePicker selected={endDate} onChange={setEndDate} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} dateFormat="yyyy-MM-dd" className="form-control" placeholderText="End date" />
            </div>
            <Button variant="outline-secondary" onClick={() => { setStartDate(null); setEndDate(null); }}>Clear</Button>
          </div>

          <div className="ag-theme-alpine" style={{ height: "75vh", width: "100%" }}>
            <AgGridReact rowHeight={52} columnDefs={columnDefs} defaultColDef={defaultColDef} pagination paginationPageSize={20} rowData={filteredData} />
          </div>
        </div>
      </div>

      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Requirement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Name *</Form.Label><Form.Control name="name" value={form.name} onChange={updateField} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>City *</Form.Label><Form.Control name="city" value={form.city} onChange={updateField} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Requirement Type *</Form.Label><Form.Select name="requirement_type" value={form.requirement_type} onChange={updateField}><option value="">Select</option>{requirementTypes.map((value) => <option key={value} value={value}>{value}</option>)}</Form.Select></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Property Type *</Form.Label><Form.Select name="property_type" value={form.property_type} onChange={updateField}><option value="">Select</option>{propertyTypes.map((value) => <option key={value} value={value}>{value}</option>)}</Form.Select></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Contact No *</Form.Label><Form.Control name="contact_no" value={form.contact_no} onChange={updateField} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Email *</Form.Label><Form.Control type="email" name="email" value={form.email} onChange={updateField} /></Form.Group></Col>
              <Col md={12}><Form.Group className="mb-3"><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={3} name="description" value={form.description} onChange={updateField} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Status</Form.Label><Form.Select name="status" value={form.status} onChange={updateField}>{statuses.map((value) => <option key={value} value={value}>{value}</option>)}</Form.Select></Form.Group></Col>
              <Col md={12}><Form.Group className="mb-3"><Form.Label>Admin Notes</Form.Label><Form.Control as="textarea" rows={3} name="notes" value={form.notes} onChange={updateField} /></Form.Group></Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveRequirement} disabled={loader}>{loader ? <Spinner size="sm" animation="border" /> : "Save"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deleteItem)} onHide={() => setDeleteItem(null)}>
        <Modal.Header closeButton><Modal.Title>Delete Requirement</Modal.Title></Modal.Header>
        <Modal.Body>Delete the requirement from {deleteItem?.name || "this client"}?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteItem(null)}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loader}>{loader ? <Spinner size="sm" animation="border" /> : "Delete"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ListRequirement;
